/*
 * Copyright (C) 2019-2022 Corvalent Corporation - All Rights Reserved
 * This software is licensed program property and is Proprietary and Confidential.
 * Unauthorized copying of this file via any medium is strictly prohibited unless
 * prior written permission has been obtained by Corvalent Corporation.
 * In addition, the look and feel of this software as well as the user interface
 * design is also Copyright ©.
 * Notice: This software is not open source.
 * This software is subject to the Corvalent Software License Agreement.
 * You should have received a copy of this license with the software.
 * If not, please write to: sales@corvalent.com
 * All information contained herein is, and remains, the property of
 * Corvalent Corporation and its suppliers, if any. This software may also
 * be covered by U.S. and Foreign Patents, patents in progress, and are
 * protected by trade secret or copyright law
 */

import React, { useCallback, useRef, useMemo, useEffect } from 'react';

import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import {
  Box,
  ListItemButton,
  Typography,
  Stack,
  Collapse,
} from '@mui/material';
import area from '@turf/area';
import { Apartment, Park } from '@mui/icons-material';

import { useGeneralContext } from '../../../../context/GeneralContext';
import areaZoom from '../../../../utils/areaZoom';
import AssetIcon from '../../../shared/atoms/AssetIcon';
import {
  closeAnimated,
  openAnimated,
} from '../../../../redux/actions/sideDrawerActions';
import CustomTooltip from '../../../shared/atoms/CustomTooltip';
import { useRolePermissions } from '../../../../hooks/useRolePermissions';
import { useConfirmation } from '../../../../context/ConfirmationContext';
import { useLocationsCacheContext } from '../../../../context/LocationsCacheContext';
import MoreOptionsMenu from '../../../shared/molecules/MoreOptionsMenu';
import { delCRUD } from '../../../../services/crudService';
import { queryClient } from '../../../../services/queryClient';
import { useAssetFormContext } from '../../../../context/AssetFormContext';
import { statusColorObj } from '../../../../themes';
import ChildrenCard from './ChildrenCard';
import Asset from './Asset';
import UploadFileContext from '../../../../context/UploadFileContext';
import PopupWrapper from '../../../Popup/PopupWrapper';
import { useIsSmallMobile } from '../../../../hooks/useIsSmallMobile';
import { useIsMobile } from '../../../../hooks/useIsMobile';

const Location = ({
  location,
  buildings,
  assets,
  onEdit,
  shouldFly,
  onClick,
}) => {
  const history = useHistory();
  const targetRef = useRef(null);
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const isSmallMobile = useIsSmallMobile();
  const { openConfirmation } = useConfirmation();
  const isDrawerOpen = useSelector(store => store.sideDrawerState.isOpen);
  const { data: userPermissions } = useRolePermissions();
  const {
    setLastSelectedLocation,
    lastSelectedLocation,
    openToast,
    buildingsExpanded,
    setBuildingsExpanded,
    assetsExpanded,
    setAssetsExpanded,
    isMapPage,
    setClickedAsset,
  } = useGeneralContext();
  const { setIsLocationClone } = useAssetFormContext();
  const { refreshMap } = useLocationsCacheContext();
  const dispatch = useDispatch();
  const closeAnimatedDispatch = useCallback(
    args => dispatch(closeAnimated(args)),
    [dispatch],
  );
  const openAnimatedDispatch = useCallback(
    args => dispatch(openAnimated(args)),
    [dispatch],
  );

  const isSelected = useMemo(
    () => location.properties.id === lastSelectedLocation?.properties.id,
    [lastSelectedLocation?.properties.id, location.properties.id],
  );

  useEffect(() => {
    if (isSelected)
      setTimeout(() => {
        if (targetRef.current)
          targetRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          });
      }, 500);
  }, [isSelected]);

  const flyToLocation = useCallback(() => {
    if (
      location.properties &&
      shouldFly &&
      history.location.pathname.includes('/portal/maps') &&
      !history.location.pathname.includes('/facilities')
    ) {
      window.map.flyTo({
        center: [location.properties.lng, location.properties.lat],
        zoom: areaZoom(area(location.geometry)),
      });
      window.map.fire('flystart');
    }
  }, [
    history.location.pathname,
    location.geometry,
    location.properties,
    shouldFly,
  ]);

  const onEditHandler = useCallback(
    buildingId => {
      if (!isMapPage) {
        openConfirmation({
          message: t('tools_menu.location.redirect_to_map', {
            type: buildingId ? 'building' : 'location',
          }),
          handleAction: () => {
            history.push('/portal/maps');
            openAnimatedDispatch();
          },
          handleClose: openAnimatedDispatch,
          actionLabel: t('components.main_list.modal.create_location.action'),
          title: t('tools_menu.location.edit_location', {
            type: buildingId ? 'building' : 'location',
          }),
        });
      } else {
        onEdit?.(location.properties.id, null, buildingId);
        flyToLocation();
      }
    },
    [
      flyToLocation,
      history,
      isMapPage,
      location.properties.id,
      onEdit,
      openAnimatedDispatch,
      openConfirmation,
      t,
    ],
  );

  const onGoToLocation = useCallback(() => {
    setLastSelectedLocation(location);

    if (!isMapPage) {
      history.push('/portal/maps');
      setTimeout(() => flyToLocation(), 1000);
    } else {
      flyToLocation();
    }
    isMobile && closeAnimatedDispatch();
  }, [
    closeAnimatedDispatch,
    flyToLocation,
    history,
    isMapPage,
    location,
    setLastSelectedLocation,
    isMobile,
  ]);

  const delAction = useCallback(
    async mode => {
      try {
        await delCRUD({
          entity: 'location',
          id: location.properties.id,
          params: { mode },
        });
        openToast(t('tools_menu.location.delete_success'));
        queryClient.removeQueries({
          queryKey: 'locationsCache',
          exact: true,
        });
        refreshMap();
      } catch (e) {
        console.error(e);
        if (e.response.status === 409) {
          setTimeout(() => {
            openConfirmation({
              title: t('tools_menu.location.delete_modal_title'),
              message: e.response.data.message.includes('table "ticket"')
                ? t('tools_menu.location.delete_modal_ticket')
                : t('tools_menu.location.delete_modal_conflict'),
              actionLabel: t('modal.ok'),
              preventClickOutside: true,
              hideCancel: true,
            });
          }, 100);
        } else {
          openToast(t('tools_menu.location.delete_error'), 'error');
        }
      }
    },
    [location.properties.id, openConfirmation, openToast, refreshMap, t],
  );

  const onDeleteHandler = useCallback(() => {
    openConfirmation({
      message: t('tools_menu.location.delete_location_confirm', {
        location: location.properties.name,
      }),
      options: [
        {
          onClick: async () => delAction('only'),
          label: t('tools_menu.location.delete_location_only'),
          warning: true,
          minWidth: '689px',
        },
        {
          onClick: async () => delAction('all'),
          label: t('tools_menu.location.delete_location_all'),
          warning: true,
          minWidth: '689px',
        },
      ],
      preventClickOutside: true,
    });
  }, [delAction, location.properties.name, openConfirmation, t]);

  const onCloneHandler = useCallback(() => {
    openConfirmation({
      message: t('tools_menu.location.clone_location_confirm', {
        location: location.properties.name,
      }),
      options: [
        {
          onClick: async () => {
            setIsLocationClone('only');
            onEdit?.(location.properties.id, 'only');
            flyToLocation();
          },
          label: t('tools_menu.location.clone_location_only'),
          minWidth: '400px',
        },
        {
          onClick: async () => {
            setIsLocationClone('all');
            onEdit?.(location.properties.id, 'all');
            flyToLocation();
          },
          label: t('tools_menu.location.clone_location_all'),
          minWidth: '400px',
        },
      ],
      preventClickOutside: true,
    });
  }, [
    flyToLocation,
    location.properties.id,
    location.properties.name,
    onEdit,
    openConfirmation,
    setIsLocationClone,
    t,
  ]);

  const moreOptions = useMemo(() => {
    let options = [];
    if (onEdit) {
      options = [
        ...options,
        {
          label: t('components.location.go_to'),
          action: onGoToLocation,
        },
      ];
    }
    if (userPermissions?.map?.manage && onEdit && !isMobile) {
      options = [
        ...options,
        {
          label: t('components.location.edit'),
          action: onEditHandler,
        },
        {
          label: t('components.location.clone'),
          action: onCloneHandler,
        },
        {
          label: t('components.location.delete'),
          action: onDeleteHandler,
        },
      ];
    }
    return options;
  }, [
    onCloneHandler,
    onDeleteHandler,
    onEdit,
    onEditHandler,
    onGoToLocation,
    t,
    isMobile,
    userPermissions?.map?.manage,
  ]);

  const onClickHandler = useCallback(() => {
    if (onClick) {
      onClick();
    } else {
      isMobile && !isSelected && closeAnimatedDispatch();
      if (!isSelected || !isDrawerOpen) {
        setLastSelectedLocation(location);
        if (lastSelectedLocation?.properties.id !== location.properties.id) {
          setBuildingsExpanded(true);
          setAssetsExpanded(true);
        }
        if (isMapPage) setClickedAsset(location);
      } else if (isSelected && isDrawerOpen) {
        setLastSelectedLocation(null);
      }
      if (!isSelected || !isDrawerOpen) {
        flyToLocation();
      }
      if (!isDrawerOpen && !isMapPage) {
        history.push('/portal/maps');
      }
    }
  }, [
    onClick,
    isMobile,
    isSelected,
    closeAnimatedDispatch,
    isDrawerOpen,
    isMapPage,
    setLastSelectedLocation,
    location,
    lastSelectedLocation?.properties.id,
    setClickedAsset,
    setBuildingsExpanded,
    setAssetsExpanded,
    flyToLocation,
    history,
  ]);

  const onActionPopup = useCallback(
    id => {
      if (id === location.properties.id) {
        onGoToLocation();
      } else {
        history.push(`/portal/maps/facilities/${id}`);
        closeAnimatedDispatch();
      }
    },
    [closeAnimatedDispatch, history, location.properties.id, onGoToLocation],
  );

  return (
    <>
      <CustomTooltip
        title={
          !isMobile && (
            <PopupWrapper
              item={location.properties}
              onAction={onActionPopup}
              hideDescription
            />
          )
        }
        leaveDelay={100}
        placement="right"
        componentsProps={{
          tooltip: {
            sx: {
              p: 0,
            },
          },
        }}
        slotProps={{
          popper: {
            modifiers: [
              {
                name: 'offset',
                options: {
                  offset: [0, -14],
                },
              },
            ],
          },
        }}
      >
        <ListItemButton
          data-testid="button"
          ref={targetRef}
          selected={isSelected && !!onEdit}
          sx={{
            padding: 0,
            width: '100%',
            display: 'flex',
            justifyContent: 'space-between',
            paddingTop: '2px',
            '&.Mui-selected': {
              backgroundColor: 'secondary.dark',
              '&:hover': {
                backgroundColor: 'secondary.main',
              },
            },
            '&:hover': {
              backgroundColor: 'secondary.dark',
            },
          }}
          onClick={onClickHandler}
        >
          <Stack
            direction="row"
            alignItems="center"
            padding={isDrawerOpen ? '0 1rem' : 0}
            width="100%"
          >
            {isDrawerOpen && (
              <AssetIcon status={location.properties.status} includeTooltip />
            )}
            <Box
              sx={{
                maxWidth: isSmallMobile ? '280px' : '190px',
                marginTop: '0.5rem',
                marginBottom: '0.5rem',
                marginLeft: isDrawerOpen ? '1rem' : 0,
                marginRight: isDrawerOpen ? '5px' : 0,
                whiteSpace: 'break-spaces',
              }}
              width="100%"
            >
              <Typography
                fontWeight={500}
                sx={{
                  wordWrap: 'break-word',
                  wordBreak: 'break-word',
                  whiteSpace: 'break-spaces',
                  color:
                    statusColorObj[
                      (location.properties.status || '').toLowerCase()
                    ],
                  filter: 'brightness(1.75)',
                  textShadow: '1px 1px 1px rgba(0, 0, 0, 0.7)',
                }}
                textAlign={isDrawerOpen ? 'left' : 'center'}
                width="100%"
              >
                {isDrawerOpen
                  ? location.properties.name
                  : location.properties.name
                      .substring(0, 5)
                      .trim()
                      .toUpperCase()}
              </Typography>
            </Box>
          </Stack>
          {isDrawerOpen && (
            <Stack direction="row" alignItems="center" paddingRight="10px">
              <MoreOptionsMenu options={moreOptions} size="small" margin="0" />
            </Stack>
          )}
        </ListItemButton>
      </CustomTooltip>
      <Collapse
        in={isSelected && isDrawerOpen && !!onEdit}
        timeout="auto"
        unmountOnExit
      >
        <Stack
          padding="0 0.5rem 0.5rem 0.5rem"
          spacing={1}
          sx={{
            backgroundColor: 'secondary.dark',
          }}
        >
          <UploadFileContext>
            <>
              <ChildrenCard
                Icon={Apartment}
                Item={Asset}
                items={buildings}
                title="buildings"
                isExpanded={buildingsExpanded}
                setIsExpanded={setBuildingsExpanded}
                onEdit={onEditHandler}
              />
              <ChildrenCard
                Icon={Park}
                Item={Asset}
                items={assets}
                title="assets"
                isExpanded={assetsExpanded}
                setIsExpanded={setAssetsExpanded}
                onEdit={onEditHandler}
              />
            </>
          </UploadFileContext>
        </Stack>
      </Collapse>
    </>
  );
};

Location.defaultProps = {
  onEdit: null,
  onClick: null,
};

Location.propTypes = {
  location: PropTypes.object.isRequired,
  buildings: PropTypes.arrayOf(PropTypes.object).isRequired,
  assets: PropTypes.arrayOf(PropTypes.object).isRequired,
  onEdit: PropTypes.func,
  onClick: PropTypes.func,
  shouldFly: PropTypes.bool.isRequired,
};

export default Location;
